/**
 * Twin batches resource — typed wrappers over `/v1/twin/batches` + the
 * server→UI mappers (B5.3).
 *
 * The list endpoint returns lightweight rows (FoundryBatchRow); the
 * per-heat trace endpoint returns the full BatchTraceData blob the
 * drill-in drawer renders.
 */

import { api } from "@/lib/api-client";
import type { BatchTraceData } from "@foundry/BatchTraceView";
import type { FoundryBatchRow } from "@/lib/widgets/__foundry_fixtures";
import { qs } from "./qs";
import type { TwinBatch, TwinBatchList } from "./types";

export const twinBatches = {
  list: (params?: { org_id?: string; limit?: number; offset?: number }) =>
    api.get<TwinBatchList>(`/v1/twin/batches${qs(params)}`),
  // The server returns BatchTraceData verbatim (opaque UI-owned JSON).
  trace: (id: string) =>
    api.get<BatchTraceData>(`/v1/twin/batches/${id}/trace`),
};

/**
 * Server TwinBatch → UI FoundryBatchRow.
 *
 * S2.3b (2026-08-03) — THE `?? 0` ON THREE CHEMISTRY/PROCESS FIELDS IS GONE.
 * `pour_temp` / `moisture` / `carbon` were coerced NULL→0 while `FoundryBatchRow`
 * typed all three as non-nullable `number`, so **the type itself forbade honesty**.
 * `twin_batches.carbon` is NULL on every new-model heat (`runs.go:1452-1453`) and
 * every captured heat (`capture_assemble.go:214-215`), so the mapper turned "nobody
 * measured carbon" into a rendered **0.00 %** — a Slop-Gate violation of the exact
 * kind this build exists to remove, and one every future consumer would inherit.
 *
 * It was LATENT, not live, and that is why the fix is the type rather than a call
 * site: measured 2026-08-03, no consumer reads these three fields
 * (`twin-aggregates.ts`, `GeneratedReportPanel.tsx` and `export-lot-dossier.ts`
 * never touch them; `batches-lots.tsx` reads the RAW server shape and already
 * guards with `!= null`; `copilot/context.ts:638` is typed on the canned
 * `FX_BATCHES` fixture, not on this row). So there was nothing to break and
 * nothing yet displaying a false zero — the landmine is disarmed before the first
 * consumer steps on it, and `null` now propagates so a renderer must decide
 * (Slop-Gate: "—", never 0).
 */
export function toBatchRow(b: TwinBatch): FoundryBatchRow {
  return {
    id: b.id,
    heatNumber: b.heat_number,
    grade: b.grade,
    shift: b.shift,
    startedAt: b.started_at,
    status: b.status,
    castingsTotal: b.castings_total,
    castingsAccepted: b.castings_accepted,
    castingsRejected: b.castings_rejected,
    defectType: b.defect_type ?? null,
    pourTemp: b.pour_temp ?? null,
    moisture: b.moisture ?? null,
    carbon: b.carbon ?? null,
    part: b.part ?? "",
    line: b.line ?? "",
    source: b.source ?? "",
    castingHolds: b.casting_holds ?? [],
  };
}
